import * as React from "react";
import {
  Badge,
  Button,
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui";
import {
  MoreHorizontal,
  CheckCircle2,
  Clock,
  User,
  Building2,
  Pencil,
  Trash2,
  RotateCcw,
  Repeat,
  UserCircle,
} from "lucide-react";
import { formatRelativeTime } from "@repo/utils";
import { EntityReferenceBadge } from "./entity-reference-badge";
import {
  getPriorityColor,
  getPriorityLabel,
  getRecurrenceLabel,
} from "@/lib/utils/reminder-format";
import type { Reminder } from "@/hooks/use-reminders";

interface ReminderCardProps {
  reminder: Reminder;
  onToggleComplete: (id: string) => void;
  onEdit: (reminder: Reminder) => void;
  onDelete: (id: string) => void;
  onViewDetail: (reminder: Reminder) => void;
  currentUserId?: string;
}

function getDueDateLabel(dueDate: string): {
  label: string;
  variant: "default" | "destructive" | "outline" | "secondary";
  className?: string;
} {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dueDate + "T00:00:00");

  const diffMs = due.getTime() - today.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return { label: `${Math.abs(diffDays)}d overdue`, variant: "destructive" };
  }
  if (diffDays === 0) {
    return {
      label: "Due today",
      variant: "outline",
      className: "border-amber-500 text-amber-600",
    };
  }
  if (diffDays === 1) {
    return {
      label: "Tomorrow",
      variant: "outline",
      className: "border-amber-400 text-amber-500",
    };
  }
  if (diffDays <= 7) {
    return { label: `In ${diffDays} days`, variant: "outline" };
  }

  return {
    label: new Date(dueDate + "T00:00:00").toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    }),
    variant: "outline",
  };
}

export function ReminderCard({
  reminder,
  onToggleComplete,
  onEdit,
  onDelete,
  onViewDetail,
  currentUserId,
}: ReminderCardProps) {
  const dueDateInfo = getDueDateLabel(reminder.due_date);
  const recurrenceLabel = getRecurrenceLabel(
    reminder.recurrence_type,
    reminder.recurrence_interval
  );
  const isCreator = currentUserId === reminder.created_by;
  const canEdit = isCreator || reminder.visibility === "pharmacy";

  return (
    <div
      data-testid={`reminder-card-${reminder.id}`}
      className={`group relative rounded-xl border border-border/60 bg-surface px-3 py-2.5 cursor-pointer transition-colors duration-150 hover:bg-muted/20 ${
        reminder.is_completed ? "opacity-60" : ""
      }`}
      onClick={() => onViewDetail(reminder)}
    >
      {/* Header: title + dropdown */}
      <div className="flex items-start justify-between gap-2">
        <h3
          className={`text-sm font-semibold leading-snug ${
            reminder.is_completed ? "line-through text-muted-foreground" : ""
          }`}
        >
          {reminder.title}
        </h3>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 -mt-0.5"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="h-3.5 w-3.5" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-[160px]">
            {canEdit && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(reminder);
                }}
              >
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onToggleComplete(reminder.id);
              }}
            >
              {reminder.is_completed ? (
                <>
                  <RotateCcw className="mr-2 h-4 w-4" />
                  Mark Incomplete
                </>
              ) : (
                <>
                  <CheckCircle2 className="mr-2 h-4 w-4" />
                  Mark Complete
                </>
              )}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                onDelete(reminder.id);
              }}
              className="text-danger focus:text-danger-foreground focus:bg-danger"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Description */}
      {reminder.description && (
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
          {reminder.description}
        </p>
      )}

      {/* Badges */}
      <div className="flex flex-wrap items-center gap-1.5 mt-2">
        <Badge variant="outline" className="gap-1 text-xs h-5 px-1.5">
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${getPriorityColor(reminder.priority)}`}
          />
          {getPriorityLabel(reminder.priority)}
        </Badge>

        <Badge
          variant={dueDateInfo.variant}
          className={`gap-1 text-xs h-5 px-1.5 ${dueDateInfo.className || ""}`}
        >
          <Clock className="h-3 w-3" />
          {dueDateInfo.label}
          {reminder.due_time && (
            <span className="ml-0.5">{reminder.due_time.slice(0, 5)}</span>
          )}
        </Badge>

        {recurrenceLabel && (
          <Badge
            variant="outline"
            className="gap-1 text-xs h-5 px-1.5 text-muted-foreground"
          >
            <Repeat className="h-3 w-3" />
            {recurrenceLabel}
          </Badge>
        )}

        <Badge variant="outline" className="gap-1 text-xs h-5 px-1.5">
          {reminder.visibility === "pharmacy" ? (
            <Building2 className="h-3 w-3" />
          ) : (
            <User className="h-3 w-3" />
          )}
          {reminder.visibility === "pharmacy" ? "Pharmacy" : "Personal"}
        </Badge>

        {reminder.reference_type && reminder.reference_id && (
          <EntityReferenceBadge
            referenceType={reminder.reference_type}
            referenceId={reminder.reference_id}
          />
        )}

        {reminder.patient_id && reminder.patient_name && (
          <Badge
            variant="outline"
            className="gap-1 text-xs h-5 px-1.5 hover:bg-muted/50 transition-colors"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <UserCircle className="h-3 w-3 text-secondary" />
            <span className="truncate max-w-[180px]">
              {reminder.patient_name}
            </span>
          </Badge>
        )}
      </div>

      {/* Completion info */}
      {reminder.is_completed && reminder.completed_by_user && (
        <div className="flex items-center gap-1.5 mt-2 text-xs text-muted-foreground">
          <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
          <span>
            Completed by{" "}
            <span className="font-medium">
              {reminder.completed_by_user.full_name}
            </span>
            {reminder.completed_at && (
              <> {formatRelativeTime(reminder.completed_at)}</>
            )}
          </span>
          {reminder.completion_note && (
            <span>&mdash; {reminder.completion_note}</span>
          )}
        </div>
      )}

      {/* Footer: creator + complete button */}
      <div className="flex items-center gap-2 mt-2">
        {reminder.created_by_user && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Avatar className="h-4 w-4">
              <AvatarFallback className="text-[8px]">
                {reminder.created_by_user.initials}
              </AvatarFallback>
            </Avatar>
            <span>{reminder.created_by_user.full_name}</span>
          </div>
        )}
        {!reminder.is_completed && (
          <Button
            size="sm"
            variant="ghost"
            className="ml-auto h-7 text-xs px-2 text-muted-foreground hover:text-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onToggleComplete(reminder.id);
            }}
          >
            <CheckCircle2 className="h-3.5 w-3.5" />
            <span className="ml-1">Complete</span>
          </Button>
        )}
      </div>
    </div>
  );
}
